// AI models and monthly AI-reply quota. ChatBizz owns the OpenAI account (platform key lives only on the
// Python backend); business admins just pick a model their plan includes and watch their usage.
// The real plan table, prices and quota counters belong to the backend — this mock mirrors the contract.
import { aiUsageStore, botsStore, planFor } from "./core";

export const PLAN_RANK = { Starter: 1, Business: 2, Enterprise: 3 };
export const AI_QUOTA = { Starter: 1000, Business: 10000, Enterprise: 50000 };
export const WARN_AT = 80; // percent

export const AI_MODELS = [
  { id: "GPT-4o mini", label: "GPT-4o mini", minPlan: "Starter", description: "Fast and economical — best for FAQs and high volume." },
  { id: "GPT-4o", label: "GPT-4o", minPlan: "Business", description: "Balanced quality and speed — recommended for most support bots." },
  { id: "GPT-4.1", label: "GPT-4.1", minPlan: "Enterprise", description: "Highest accuracy for complex, multi-step questions." }
];

export const modelAvailable = (id, plan) => {
  const m = AI_MODELS.find((x) => x.id === id);
  return !!m && PLAN_RANK[plan] >= PLAN_RANK[m.minPlan];
};
export const defaultModelFor = (plan) => (modelAvailable("GPT-4o", plan) ? "GPT-4o" : "GPT-4o mini");
export const modelsFor = (plan) => AI_MODELS.map((m) => ({ ...m, available: modelAvailable(m.id, plan) }));

// Demo tenants start the month partway through their quota so the meter looks real (Initech is close to its limit).
const BASELINE = { "tenant-acme": 4180, "tenant-globex": 21500, "tenant-initech": 940 };

const periodNow = () => new Date().toISOString().slice(0, 7);
const periodLabel = (p) => new Date(`${p}-01T00:00:00Z`).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const nextReset = (p) => { const [y, m] = p.split("-").map(Number); return new Date(Date.UTC(y, m, 1)).toISOString(); };

function load(tenantId) {
  const all = aiUsageStore.all();
  const period = periodNow();
  let rec = all[tenantId];
  if (!rec || rec.period !== period) {
    const first = botsStore.all().find((b) => b.tenantId === tenantId);
    const used = rec ? 0 : BASELINE[tenantId] || 0;
    rec = { period, used, byBot: first && used ? { [first.id]: used } : {} };
  }
  return { all, rec };
}

export function usageSummary(tenantId) {
  const plan = planFor(tenantId);
  const { rec } = load(tenantId);
  const quota = AI_QUOTA[plan] || AI_QUOTA.Starter;
  const percent = Math.min(100, Math.round((rec.used / quota) * 100));
  const status = rec.used >= quota ? "exceeded" : percent >= WARN_AT ? "warning" : "ok";
  const bots = botsStore.all().filter((b) => b.tenantId === tenantId);
  return {
    plan, period: rec.period, periodLabel: periodLabel(rec.period), resetsOn: nextReset(rec.period),
    used: rec.used, quota, remaining: Math.max(0, quota - rec.used), percent, status, warnAt: WARN_AT,
    byBot: bots.map((b) => ({ botId: b.id, name: b.name, model: b.model, used: rec.byBot[b.id] || 0 }))
  };
}

/** Counts one AI reply against the workspace. Returns { allowed:false } (and counts nothing) once the quota is used up. */
export function consumeAiReply(tenantId, botId) {
  const { all, rec } = load(tenantId);
  const quota = AI_QUOTA[planFor(tenantId)] || AI_QUOTA.Starter;
  if (rec.used >= quota) return { allowed: false };
  rec.used += 1;
  rec.byBot[botId] = (rec.byBot[botId] || 0) + 1;
  aiUsageStore.save({ ...all, [tenantId]: rec });
  return { allowed: true };
}
